import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// Types
export interface MindmapNode {
  id: string;
  variableId: string;
  title: string;
  description: string;
  slug: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  forecolor: string;
  isRoot: boolean;
  isDeletable: boolean;
  parentId: string | null;
  children: string[];
  collapsed?: boolean;
  created_at: string;
}

export interface MindmapConnection {
  id: string;
  sourceId: string;
  targetId: string;
  created_at: string;
}

export interface Variable {
  _id: string;
  prompt_category_id: string;
  title: string;
  description: string;
  type_id: string;
  icon: string | null;
  slug: string;
  filterable: boolean;
  visible_on_card: boolean;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: string | null;
  deleted_by: string | null;
  is_trash: boolean;
  __v: number;
}

// Helper function to generate unique IDs
const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to generate random pastel colors
const getRandomColor = () => {
  const colors = [
    '#FFE5E5', // Light Pink
    '#E5F3FF', // Light Blue
    '#E5FFE5', // Light Green
    '#FFF5E5', // Light Orange
    '#F3E5FF', // Light Purple
    '#FFFFE5', // Light Yellow
    '#E5FFFF', // Light Cyan
    '#FFE5F3', // Light Magenta
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

export const useMindmapStore = defineStore('mindmap', () => {
  // State
  const nodes = ref<Map<string, MindmapNode>>(new Map());
  const connections = ref<Map<string, MindmapConnection>>(new Map());
  const selectedNodeId = ref<string | null>(null);
  const canvasOffset = ref({ x: 0, y: 0 });
  const canvasScale = ref(1);

  // Computed
  const allNodes = computed(() => Array.from(nodes.value.values()));
  const rootNodes = computed(() => allNodes.value.filter(node => node.isRoot));
  const selectedNode = computed(() => 
    selectedNodeId.value ? nodes.value.get(selectedNodeId.value) : null
  );
  const nodeCount = computed(() => nodes.value.size);
  const connectionCount = computed(() => connections.value.size);

  // Actions
  const createRootNode = (variable: Variable, position: { x: number; y: number }): MindmapNode => {
    const nodeId = generateId();
    
    const newNode: MindmapNode = {
      id: nodeId,
      variableId: variable._id,
      title: variable.title,
      description: variable.description || '',
      slug: variable.slug || '',
      x: position.x,
      y: position.y,
      width: 200,
      height: 100,
      color: getRandomColor(),
      forecolor: '#212529',
      isRoot: true,
      isDeletable: true,
      parentId: null,
      children: [],
      created_at: new Date().toISOString(),
    };

    nodes.value.set(nodeId, newNode);
    console.log('Created root node:', newNode);
    
    return newNode;
  };

  const createChildNode = (
    variable: Variable, 
    parentId: string, 
    position: { x: number; y: number }
  ): MindmapNode | null => {
    const parent = nodes.value.get(parentId);
    if (!parent) {
      console.error('Parent node not found:', parentId);
      return null;
    }

    const nodeId = generateId();
    
    const newNode: MindmapNode = {
      id: nodeId,
      variableId: variable._id,
      title: variable.title,
      description: variable.description || '',
      slug: variable.slug || '',
      x: position.x,
      y: position.y,
      width: 180,
      height: 80,
      color: getRandomColor(),
      forecolor: '#212529',
      isRoot: false,
      isDeletable: true,
      parentId: parentId,
      children: [],
      created_at: new Date().toISOString(),
    };

    nodes.value.set(nodeId, newNode);
    
    // Update parent's children array
    parent.children.push(nodeId);
    
    // Create connection
    createConnection(parentId, nodeId);
    
    console.log('Created child node:', newNode);
    
    return newNode;
  };

  const createConnection = (sourceId: string, targetId: string): MindmapConnection => {
    const connectionId = `conn_${sourceId}_${targetId}`;
    
    const newConnection: MindmapConnection = {
      id: connectionId,
      sourceId,
      targetId,
      created_at: new Date().toISOString(),
    };

    connections.value.set(connectionId, newConnection);
    console.log('Created connection:', newConnection);
    
    return newConnection;
  };

  const createManualConnection = (sourceId: string, targetId: string): MindmapConnection | null => {
    // Check if nodes exist
    const sourceNode = nodes.value.get(sourceId);
    const targetNode = nodes.value.get(targetId);
    
    if (!sourceNode || !targetNode) {
      console.error('Source or target node not found');
      return null;
    }
    
    // Check if connection already exists
    const existingConnection = Array.from(connections.value.values()).find(
      conn => conn.sourceId === sourceId && conn.targetId === targetId
    );
    
    if (existingConnection) {
      console.warn('Connection already exists');
      return null;
    }
    
    return createConnection(sourceId, targetId);
  };

  const deleteConnection = (connectionId: string): boolean => {
    const connection = connections.value.get(connectionId);
    if (!connection) {
      console.error('Connection not found:', connectionId);
      return false;
    }
    
    connections.value.delete(connectionId);
    console.log('Deleted connection:', connectionId);
    return true;
  };

  const updateNodePosition = (nodeId: string, position: { x: number; y: number }) => {
    const node = nodes.value.get(nodeId);
    if (node) {
      node.x = position.x;
      node.y = position.y;
    }
  };

  const updateNodeColor = (nodeId: string, color: string) => {
    const node = nodes.value.get(nodeId);
    if (node) {
      node.color = color;
      console.log(`Updated node ${nodeId} color to ${color}`);
    }
  };

  const updateNodeTitle = (nodeId: string, title: string) => {
    const node = nodes.value.get(nodeId);
    if (node) {
      node.title = title;
      console.log(`Updated node ${nodeId} title`);
    }
  };

  const updateNodeDescription = (nodeId: string, description: string) => {
    const node = nodes.value.get(nodeId);
    if (node) {
      node.description = description;
      console.log(`Updated node ${nodeId} description`);
    }
  };

  const updateNodeForecolor = (nodeId: string, forecolor: string) => {
    const node = nodes.value.get(nodeId);
    if (node) {
      node.forecolor = forecolor;
      console.log(`Updated node ${nodeId} forecolor to ${forecolor}`);
    }
  };

  const selectNode = (nodeId: string | null) => {
    selectedNodeId.value = nodeId;
  };

  const deleteNode = (nodeId: string) => {
    const node = nodes.value.get(nodeId);
    if (!node) return;

    // Delete all children recursively
    node.children.forEach(childId => deleteNode(childId));

    // Remove connections
    const connectionsToDelete: string[] = [];
    connections.value.forEach((conn, connId) => {
      if (conn.sourceId === nodeId || conn.targetId === nodeId) {
        connectionsToDelete.push(connId);
      }
    });
    connectionsToDelete.forEach(connId => connections.value.delete(connId));

    // Update parent's children array
    if (node.parentId) {
      const parent = nodes.value.get(node.parentId);
      if (parent) {
        parent.children = parent.children.filter(id => id !== nodeId);
      }
    }

    // Delete the node
    nodes.value.delete(nodeId);
    
    if (selectedNodeId.value === nodeId) {
      selectedNodeId.value = null;
    }

    console.log('Deleted node:', nodeId);
  };

  const toggleNodeCollapse = (nodeId: string) => {
    const node = nodes.value.get(nodeId);
    if (!node) return;
    
    // Toggle collapsed state
    node.collapsed = !node.collapsed;
  };

  const clearCanvas = () => {
    // Check if start node exists before clearing
    const startNode = nodes.value.get('start_node');
    
    nodes.value.clear();
    connections.value.clear();
    selectedNodeId.value = null;
    canvasOffset.value = { x: 0, y: 0 };
    canvasScale.value = 1;
    
    // Always ensure start node exists after clearing
    if (startNode) {
      nodes.value.set('start_node', startNode);
    } else {
      createStartNode();
    }
    
    console.log('Canvas cleared, start node preserved');
  };

  const createStartNode = (): MindmapNode => {
    const startNode: MindmapNode = {
      id: 'start_node',
      variableId: 'start',
      title: 'Start',
      description: 'I want to you to act like a',
      slug: 'start',
      x: 400,
      y: 300,
      width: 250,
      height: 120,
      color: '#d1f2eb',
      forecolor: '#212529',
      isRoot: true,
      isDeletable: false,
      parentId: null,
      children: [],
      created_at: new Date().toISOString(),
    };
    nodes.value.set(startNode.id, startNode);
    console.log('Created start node');
    return startNode;
  };

  const setCanvasTransform = (offset: { x: number; y: number }, scale: number) => {
    canvasOffset.value = offset;
    canvasScale.value = scale;
  };

  const getNodeById = (nodeId: string): MindmapNode | undefined => {
    return nodes.value.get(nodeId);
  };

  const getNodeChildren = (nodeId: string): MindmapNode[] => {
    const node = nodes.value.get(nodeId);
    if (!node) return [];
    
    return node.children
      .map(childId => nodes.value.get(childId))
      .filter((child): child is MindmapNode => child !== undefined);
  };

  const exportMindmap = () => {
    return {
      nodes: Array.from(nodes.value.values()),
      connections: Array.from(connections.value.values()),
      metadata: {
        created_at: new Date().toISOString(),
        node_count: nodeCount.value,
        connection_count: connectionCount.value,
      },
    };
  };

  const importMindmap = (data: any) => {
    try {
      console.log('importMindmap called with data:', data);
      console.log('Number of nodes to import:', data.nodes?.length || 0);
      
      // Clear without recreating start node yet
      nodes.value.clear();
      connections.value.clear();
      selectedNodeId.value = null;
      
      if (data.nodes && Array.isArray(data.nodes)) {
        console.log('Importing nodes...');
        data.nodes.forEach((node: MindmapNode) => {
          console.log('Importing node:', node.id, node.title);
          nodes.value.set(node.id, node);
        });
      }
      
      if (data.connections && Array.isArray(data.connections)) {
        data.connections.forEach((conn: MindmapConnection) => {
          connections.value.set(conn.id, conn);
        });
      }
      
      console.log('Nodes in store after import:', nodes.value.size);
      console.log('All node IDs:', Array.from(nodes.value.keys()));
      
      // Ensure start node exists after import
      const hasStartNode = nodes.value.has('start_node');
      console.log('Has start_node?', hasStartNode);
      
      if (!hasStartNode) {
        console.log('Start node not found in imported data, creating one');
        createStartNode();
        console.log('Start node created, total nodes now:', nodes.value.size);
      }
      
      console.log('Mindmap imported successfully, final nodes count:', nodes.value.size);
      return true;
    } catch (error) {
      console.error('Error importing mindmap:', error);
      return false;
    }
  };

  return {
    // State
    nodes,
    connections,
    selectedNodeId,
    canvasOffset,
    canvasScale,
    
    // Computed
    allNodes,
    rootNodes,
    selectedNode,
    nodeCount,
    connectionCount,
    
    // Actions
    createRootNode,
    createChildNode,
    createStartNode,
    createConnection,
    createManualConnection,
    deleteConnection,
    updateNodePosition,
    updateNodeColor,
    updateNodeTitle,
    updateNodeForecolor,
    updateNodeDescription,
    selectNode,
    deleteNode,
    toggleNodeCollapse,
    clearCanvas,
    setCanvasTransform,
    getNodeById,
    getNodeChildren,
    exportMindmap,
    importMindmap,
  };
});
